import React from "react";
import { motion } from "framer-motion";
import { Flame, Leaf, Soup, Wheat } from "lucide-react";
import { KITCHEN_IMAGE, HERO_IMAGE, RESTAURANT } from "@/data/menu";
import Divider from "@/components/Divider";

const SPECIALTIES = [
  { icon: Flame, title: "Tandoori", desc: "Clay-oven kissed kababs, chops & tandoori chicken — smoky, marinated overnight." },
  { icon: Wheat, title: "Biryanis", desc: "Layered saffron basmati slow-cooked with goat, lamb, shrimp or garden vegetables." },
  { icon: Soup, title: "South Indian", desc: "Kerala pepper, Andhra heat, Goan coconut — coastal flavors with deep regional roots." },
  { icon: Leaf, title: "Vegetarian", desc: "Palak paneer, malai kofta, daal makhanwala — slow-cooked vegetarian comfort." },
];

export default function About() {
  return (
    <div data-testid="page-about" className="bg-[#FBF8F3]">
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[460px] overflow-hidden pt-20 md:pt-0" data-testid="about-hero">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1025]/85 via-[#1A1025]/55 to-[#1A1025]/85" />
        <div className="relative h-full flex flex-col justify-center px-6 md:px-12 lg:px-24 max-w-[1500px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="eyebrow text-[#F5C518] mb-5 md:mb-6">Our Story</div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#FBF8F3] leading-[1] md:leading-[0.95] tracking-tight max-w-3xl">
              A kitchen built on <span className="italic text-[#F5C518]">memory</span>.
            </h1>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="about-story">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="eyebrow mb-6">Authentic. Warm. Personal.</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2A1B3D] leading-[1.05] tracking-tight">
              Spices have <br /><span className="italic">a memory</span>.
            </h2>
            <div className="mt-10 hidden lg:block">
              <img
                src="https://images.pexels.com/photos/33947401/pexels-photo-33947401.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Biryani"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 text-[#4A3F55] text-lg leading-relaxed space-y-6"
          >
            <p>
              7 Spices began with a simple idea: bring the layered, soulful cooking of
              India&apos;s home kitchens to Coral Springs — without compromise. Our seven-spice
              blend is the through-line that ties our menu together: cumin, coriander,
              cardamom, cloves, cinnamon, black pepper and a quiet whisper of fennel.
            </p>
            <p>
              Each curry is built from the bottom up. Onions are caramelized for an hour
              before tomato joins the pan. Tandoori marinades sit overnight in yogurt and
              ginger. Biryanis are layered, sealed, and finished on the dum — a slow
              steam that lets every grain stand on its own.
            </p>
            <p>
              We cook the way our grandmothers cooked: slowly, patiently, and with the
              kind of generosity that makes a meal feel like a homecoming.
            </p>
            <div className="pt-4">
              <Divider className="text-[#2A1B3D]/40 justify-start" />
            </div>
            <p className="font-serif italic text-2xl text-[#2A1B3D] leading-snug">
              &ldquo;The best Indian food doesn&apos;t try to impress you. It just remembers you.&rdquo;
            </p>
          </motion.div>
        </div>
      </section>

      {/* SPECIALTIES BENTO */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#FFFFFF]" data-testid="about-specialties">
        <div className="max-w-[1500px] mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="eyebrow mb-5">What We&apos;re Known For</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2A1B3D] tracking-tight leading-[1.05]">
              Four kitchens, <span className="italic">one table</span>.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[rgba(42,27,61,0.12)]">
            {SPECIALTIES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-8 lg:p-10 border-b border-r border-[rgba(42,27,61,0.12)] hover:bg-[#FBF8F3] transition-colors group"
                data-testid={`specialty-${s.title.toLowerCase()}`}
              >
                <s.icon className="w-7 h-7 text-[#E91E63] mb-6 group-hover:text-[#2A1B3D] transition-colors" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl lg:text-3xl text-[#2A1B3D] mb-3">{s.title}</h3>
                <p className="text-sm text-[#4A3F55] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KITCHEN IMAGE STRIP */}
      <section className="relative h-[60vh] min-h-[440px] overflow-hidden" data-testid="about-kitchen">
        <img src={KITCHEN_IMAGE} alt="Our kitchen" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1025]/85 to-[#1A1025]/20" />
        <div className="relative h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 max-w-[1500px] mx-auto pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="eyebrow text-[#F5C518] mb-5">In The Kitchen</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#FBF8F3] leading-tight max-w-3xl">
              Open flames. <span className="italic">Quiet hands.</span> Loud flavors.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* SERVICE NARRATIVE */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="about-service">
        <div className="max-w-4xl mx-auto text-center">
          <div className="eyebrow mb-5">Dine-in · Takeout · Delivery</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2A1B3D] tracking-tight leading-[1.05]">
            However you eat, <br />
            <span className="italic">we&apos;ll take care of it.</span>
          </h2>
          <p className="mt-8 text-lg text-[#4A3F55] leading-relaxed max-w-2xl mx-auto">
            Most dishes can be made <strong className="text-[#E91E63]">mild, medium, or hot</strong> to your
            preference. Tell your server — or note it on your takeout call — and we&apos;ll
            adjust the spice level just for you.
          </p>
          <p className="mt-4 text-sm text-[#4A3F55]/80">
            18% gratuity added on parties of 4 or more. Please let us know about any allergies.
          </p>
        </div>
      </section>
    </div>
  );
}
